/**
 * Owner: Haselton Baker Risk Group, LLC
 * Copyright All Rights Reserved
 */
// @flow
import type { Element } from 'react';

import get from 'lodash/fp/get.js';
import capitalize from 'lodash/fp/capitalize.js';
import classNames from 'classnames';
import React from 'react';
import {
  STRUCTURAL_RESPONSE_DERIVED_FROM,
  STRUCTURAL_RESPONSE_EDP_KEY,
  STRUCTURAL_RESPONSE_ITEM_KEY,
  STRUCTURAL_RESPONSE_NAME,
  STRUCTURAL_RESPONSE_USE_TO_DEFAULT_COMPONENTS,
} from '@hbrisk/sp3-risk-model-support/models/app/attributes/names/index.js';
import SortableTable from '#components/support/table/SortableTable/index.jsx';
import LinkCell from './LinkCell/index.jsx';
import EdpCell from './EdpCell/index.jsx';
import ActionCell from './ActionCell/index.js';
import {
  responsesTable,
  colName,
  colAbbreviation,
  colMethodDescription,
  colUseToDefaultComponents,
  colDerivedFrom,
  colAction,
} from './StructuralResponsesTable.module.scss';

// Convert react-table v6 columns to TanStack Table v8 format
const convertColumn = (oldColumn: any) => {
  const {
    id,
    accessor,
    Header,
    Cell,
    sortable = true,
    className,
    headerClassName,
    show = true,
  } = oldColumn;

  // Filter out columns with show: false
  if (show === false) {
    return null;
  }

  return {
    id,
    accessorKey: typeof accessor === 'string' ? accessor : undefined,
    accessorFn: typeof accessor === 'function' ? accessor : undefined,
    header: Header,
    enableSorting: sortable,
    // Only add cell if a custom Cell component is provided
    ...(Cell
      ? {
          cell: info => (
            <Cell value={info.getValue()} original={info.row.original} row={info.row.original} />
          ),
        }
      : {}),
    meta: {
      className,
      headerClassName,
    },
  };
};

const myColumns = (form, needsDelete) =>
  [
    {
      id: STRUCTURAL_RESPONSE_NAME,
      accessor: STRUCTURAL_RESPONSE_NAME,
      Header: 'Structural Response',
      Cell: LinkCell,
      className: colName,
      headerClassName: colName,
    },
    {
      id: 'abbreviation',
      accessor: 'abbreviation',
      Header: 'EDP',
      Cell: EdpCell,
      className: colAbbreviation,
      headerClassName: colAbbreviation,
    },
    {
      id: 'methodDescription',
      accessor: 'methodDescription',
      Header: 'Source',
      className: colMethodDescription,
      headerClassName: colMethodDescription,
    },
    {
      id: STRUCTURAL_RESPONSE_USE_TO_DEFAULT_COMPONENTS,
      accessor: STRUCTURAL_RESPONSE_USE_TO_DEFAULT_COMPONENTS,
      Header: <div className="text-wrap">Default for Components?</div>,
      className: colUseToDefaultComponents,
      headerClassName: colUseToDefaultComponents,
      Cell: data => {
        const useToDefaultComponents = get(
          `original.${STRUCTURAL_RESPONSE_USE_TO_DEFAULT_COMPONENTS}`,
          data
        );
        return <div className="text-center">{capitalize(useToDefaultComponents)}</div>;
      },
    },
    {
      id: STRUCTURAL_RESPONSE_DERIVED_FROM,
      accessor: 'derivedFromName',
      Header: 'Derived From',
      className: colDerivedFrom,
      headerClassName: colDerivedFrom,
      Cell: data => {
        const derivedFromName = get('original.derivedFromName', data);
        return <div className="text-wrap">{derivedFromName}</div>;
      },
    },
    {
      Header: 'Action',
      id: 'action',
      sortable: false,
      className: classNames('p-0', colAction),
      headerClassName: colAction,
      show: needsDelete,
      Cell: (data: Object) => {
        const edpKey = get(`original.${STRUCTURAL_RESPONSE_EDP_KEY}`, data);
        const itemKey = get(`original.${STRUCTURAL_RESPONSE_ITEM_KEY}`, data);
        const index = get('original.responseIndex', data);
        const exceedsRequirement = get('original.exceedsRequirement', data);
        const hasComponents = get('original.hasComponents', data);
        return (
          <ActionCell
            edpKey={edpKey}
            index={index}
            form={form}
            itemKey={itemKey}
            exceedsRequirement={exceedsRequirement}
            hasComponents={hasComponents}
          />
        );
      },
    },
  ]
    .map(convertColumn)
    .filter(Boolean);

type StructuralResponse = {
  index: number,
  edpKey: string,
  name: string,
  method: string,
  methodDescription: string,
};

type Props = {|
  data: Array<StructuralResponse>,
  needsDelete: boolean,
  form: string,
|};

const StructuralResponsesTable = (props: Props): Element<typeof SortableTable | 'div'> => {
  const { data, needsDelete, form } = props;
  const columns = React.useMemo(() => myColumns(form, needsDelete), [form, needsDelete]);
  const getRowId = React.useCallback(row => `row-${row[STRUCTURAL_RESPONSE_ITEM_KEY]}`, []);

  return (
    <SortableTable
      data={data}
      columns={columns}
      initialSorting={[]}
      className={classNames(['responses-table', responsesTable])}
      getRowId={getRowId}
      tableId="structural-responses-table"
    />
  );
};
export default StructuralResponsesTable;
